import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTrackCtaPressed } from "../../../tracking";
import {
  ArrowIcon,
  Button,
  ButtonContainer,
  Container,
  TripCostActions,
} from "./styles";
import { useTranslation } from "react-i18next";
import { Popup } from "components/popup";
import { PopupBody } from "components/popup/body";
import { PopupButton } from "components/popup/button";
import { PopupHeader } from "components/popup/header";
import { AddOnItems } from "screens/offer-bc/body/add-ons/add-on-items";
import { useActiveCoverage } from "screens/offer-bc/utils";
import { useAddOnsStrings } from "screens/offer/utils";
import { Price } from "./price";
import { OfferContext } from "screens/offer-bc";
import { trackComponent, trackInteraction } from "utils/tracking/mixpanel";
import { useStatsigExperiment } from "hooks/experiments/use-statsig-experiments";

export function TripCost() {
  const { invalidTripCost } = useContext(OfferContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const addOnsStrings = useAddOnsStrings();
  const hasActiveAddedCoverage = useActiveCoverage();
  const { getExperimentValue } = useStatsigExperiment<{
    addOnsLocation: "addonsInPopup" | "addonsBelowCta";
  }>();
  const { addOnsLocation = "addonsBelowCta" } = getExperimentValue(
    "exp_desktop_offer_page_layout_shift",
  );

  const navigate = useNavigate();
  const { t } = useTranslation("screens", { keyPrefix: "offer.tripCost" });

  const ctaText =
    addOnsLocation === "addonsBelowCta" || hasActiveAddedCoverage
      ? t("ctaButton")
      : t("ctaButtonSkipAlt");

  function toggleDialog() {
    setIsDialogOpen((p) => !p);
  }

  function onPopupClose() {
    toggleDialog();
    trackInteraction("Click Exit Add Ons Page", {
      interaction_type: "button",
      name_of_page: "Offer Page",
      interaction_region: "top_right",
      component_name: "Add ons Popup",
    });
  }

  function handleOfferCtaClick() {
    if (addOnsLocation === "addonsInPopup") {
      toggleDialog();
      trackInteraction("Clicked Offer CTA", {
        interaction_type: "button",
        interaction_region: "inside trip summary",
        interaction_text: "Get covered",
        name_of_page: "Offer Page",
        event_name: "Clicked Offer CTA",
      });
      trackComponent("Add ons Popup", {
        component_name: "Add ons Popup",
        component_type: "popup",
        component_region: "inside trip summary",
        name_of_page: "Offer Page",
      });
    } else {
      trackCtaPressed();
      navigateToCheckout();
    }
  }

  function navigateToCheckout() {
    navigate("/checkout");
  }

  function handleAddOnsCtaClick() {
    trackCtaPressed();
    navigateToCheckout();
  }

  const trackCtaPressed = useTrackCtaPressed(
    addOnsLocation === "addonsInPopup"
      ? {
          interaction_region: "inside add ons popup",
          interaction_text: ctaText,
          name_of_page: "Offer Page",
          event_name: "Click Add ons CTA",
        }
      : {
          interaction_region: "inside trip summary",
          interaction_text: "Get covered",
          name_of_page: "Offer Page",
          event_name: "Clicked Offer CTA",
        },
  );

  return (
    <Container>
      <Price />
      <ButtonContainer>
        <Button disabled={invalidTripCost?.value} onClick={handleOfferCtaClick}>
          {t("ctaButton")}
          <ArrowIcon />
        </Button>
      </ButtonContainer>
      {addOnsLocation === "addonsInPopup" && (
        <Popup open={isDialogOpen} maxWidth="lg" fullWidth>
          <PopupHeader
            title="Anything to add on?"
            onClose={onPopupClose}
            screen
          />
          <PopupBody screen>
            <AddOnItems
              addOns={[...addOnsStrings.addOns, ...addOnsStrings.extraAddOns]}
              textCta
              columns
              inlineActionInfo
            />
          </PopupBody>
          <TripCostActions>
            <Price startAdornment={t("total")} uniformHeight />
            <PopupButton
              variant={hasActiveAddedCoverage ? "standard" : "outlined"}
              type="submit"
              onClick={handleAddOnsCtaClick}
            >
              {ctaText}
            </PopupButton>
          </TripCostActions>
        </Popup>
      )}
    </Container>
  );
}
